
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

const services = [
  {
    icon: 'ri-home-gear-line',
    title: 'Interior Improvements',
    description:
      'We specialize in customizing your interior needs to make your dream home come alive. From kitchen renovations to bathroom remodels, we handle it all with precision and care.',
    features: [
      'Kitchen Remodeling',
      'Bathroom Renovations',
      'Flooring Installation',
      'Interior Painting',
      'Drywall Repair',
      'Crown Molding',
      'Granite & Quartz Installation',
    ],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Exterior Projects',
    // Fixed escaped apostrophe – a single back‑slash is enough inside double quotes
    description:
      "Receive exterior services that will liven up your home's appearance. We provide comprehensive exterior solutions to enhance curb appeal and protect your investment.",
    features: [
      'Siding Installation',
      'Deck Building',
      'Fence Installation',
      'Exterior Painting',
      'Gutter Services',
      'Pressure Washing',
      'Storm Protection',
    ],
  },
  {
    icon: 'ri-tools-line',
    title: 'Handyman Services',
    description:
      'Taking care of all your home maintenance needs from Honey-Do Lists to serious repairs. No job is too small for our experienced team in Mobile & Baldwin Counties.',
    features: [
      'General Repairs',
      'Door Installation',
      'Window Repairs',
      'Fixture Installation',
      'Shelving & Storage',
      'Minor Plumbing',
    ],
  },
  {
    icon: 'ri-hammer-line',
    title: 'Professional Contracting',
    description:
      'Whether it be a new addition, current remodeling project, damage repairs or more, we are ready to meet your needs with our professional experience & home expertise.',
    features: [
      'Home Additions',
      'Room Conversions',
      'Structural Repairs',
      'Storm Damage Repair',
      'Insurance Claims',
      'Custom Projects',
    ],
  },
];

export default function ServicesPage() {
  // Wrap useSEO in a try/catch to avoid breaking the page if SEO generation fails
  try {
    useSEO({
      title:
        'Home Improvement Services Mobile AL | Remodeling Contractor Baldwin County',
      description:
        'Professional home improvement services in Mobile & Baldwin Counties AL. Interior remodeling, exterior projects, handyman services, kitchen & bathroom renovation. Licensed & insured. Call 251-348-8514.',
      keywords:
        'home improvement services Mobile AL, interior remodeling Baldwin County, exterior contractor Alabama, kitchen remodeling, bathroom renovation, handyman Mobile County',
      canonical: '/services',
      ogType: 'website',
      schemas: [
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Home Improvement & Remodeling',
          provider: {
            '@type': 'GeneralContractor',
            '@id': 'https://www.slcontractorsofal.com/#organization',
            name: 'S&L Contractors of Alabama, Inc.',
            telephone: '+1-251-348-8514',
            email: 'SL.Contractors.AL@gmail.com',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Mobile',
              addressRegion: 'AL',
              postalCode: '36609',
              addressCountry: 'US',
            },
          },
          areaServed: [
            { '@type': 'City', name: 'Mobile' },
            { '@type': 'AdministrativeArea', name: 'Baldwin County' },
            { '@type': 'AdministrativeArea', name: 'Mobile County' },
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Home Improvement Services',
            itemListElement: services.map((s, i) => ({
              '@type': 'OfferCatalog',
              name: s.title,
              position: i + 1,
              itemListElement: s.features.map((f) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', name: f },
              })),
            })),
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.slcontractorsofal.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Services',
              item: 'https://www.slcontractorsofal.com/services',
            },
          ],
        },
      ],
    });
  } catch (err) {
    console.error('SEO configuration failed:', err);
  }

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />

      {/* Breadcrumb */}
      <nav
        className="pt-24 pb-2 px-4 max-w-7xl mx-auto"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center text-sm text-gray-500 space-x-2">
          <li>
            <Link to="/" className="hover:text-sky-600">
              Home
            </Link>
          </li>
          <li>
            <span>/</span>
          </li>
          <li className="text-gray-900 font-medium">Services</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <header className="pt-4 pb-20 px-4 bg-gradient-to-br from-[#ebe5d9] to-[#f5f0e6]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Home Improvement Services in Mobile &amp; Baldwin Counties
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            With over 20 years of experience, we provide comprehensive{' '}
            <strong>home improvement</strong>, <strong>remodeling</strong>, and
            repair services throughout{' '}
            <strong>Baldwin &amp; Mobile Counties</strong>, Alabama.
          </p>
        </div>
      </header>

      {/* Services Grid */}
      <main className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <article
                key={service.title + index}
                className="bg-[#faf7f2] border border-[#e5dfd3] rounded-lg p-8 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-sky-50 rounded-lg mb-6">
                  <i
                    className={`${service.icon} text-3xl text-sky-600`}
                    aria-hidden="true"
                  ></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div
                      key={feature + idx}
                      className="flex items-center text-sm text-gray-700"
                    >
                      <i className="ri-check-line text-sky-500 mr-2"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      {/* Why Choose Us */}
      <section
        className="py-20 px-4 bg-[#ebe5d9]"
        aria-label="Why choose S&L Contractors"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            Why Choose S&amp;L Contractors?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="text-center p-8">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6"
                style={{ backgroundColor: '#1e3a5f' }}
              >
                <i className="ri-award-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <Link
                  to="/gallery"
                  className="no-underline hover:text-sky-600 transition-colors"
                >
                  20+ Years Experience
                </Link>
              </h4>
              <p className="text-gray-600">
                Decades of expertise in{' '}
                <strong>residential contracting</strong> and home improvement
                services in Mobile AL.
              </p>
            </article>
            <article className="text-center p-8">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6"
                style={{ backgroundColor: '#1e3a5f' }}
              >
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <Link
                  to="/contact"
                  className="no-underline hover:text-sky-600 transition-colors"
                >
                  Licensed &amp; Insured
                </Link>
              </h4>
              <p className="text-gray-600">
                Fully licensed and insured for your peace of mind and protection
                on every <strong>remodeling</strong> project.
              </p>
            </article>
            <article className="text-center p-8">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6"
                style={{ backgroundColor: '#1e3a5f' }}
              >
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                <Link
                  to="/gallery"
                  className="no-underline hover:text-sky-600 transition-colors"
                >
                  Quality Guaranteed
                </Link>
              </h4>
              <p className="text-gray-600">
                We stand behind our work with excellence and integrity in every{' '}
                <strong>home improvement</strong> project.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#1e3a5f' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Start Your Home Improvement Project?
          </h2>
          <p className="text-sky-100 text-lg mb-8">
            Contact us today for a free estimate on your{' '}
            <strong className="text-white">remodeling</strong> or repair project
            in Mobile &amp; Baldwin Counties.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 rounded-md font-semibold transition-all whitespace-nowrap"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
